import React from 'react';

/**
 * Simple Line Chart Component (Pure CSS/SVG implementation)
 * No external charting library needed - lightweight and customizable
 */
export const LineChart = ({ data, title, color = '#3B82F6', height = 200 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg">
        <p className="text-gray-500">אין נתונים להצגה</p>
      </div>
    );
  }

  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue || 1;

  const points = data.map((item, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 100 - ((item.value - minValue) / range) * 80;
    return `${x},${y + 10}`;
  }).join(' ');

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      {title && <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>}
      
      <div className="relative" style={{ height }}>
        <svg viewBox="0 0 100 100" className="w-full h-full">
          {/* Grid lines */}
          <defs>
            <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#E5E7EB" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#grid)" />
          
          {/* Chart line */}
          <polyline
            fill="none"
            stroke={color}
            strokeWidth="2"
            points={points}
            className="drop-shadow-sm"
          />
          
          {/* Data points */}
          {data.map((item, index) => {
            const x = (index / (data.length - 1)) * 100;
            const y = 100 - ((item.value - minValue) / range) * 80 + 10;
            return (
              <circle
                key={index}
                cx={x}
                cy={y}
                r="3"
                fill={color}
                className="hover:r-4 transition-all duration-200 cursor-pointer"
              >
                <title>{`${item.label}: ${item.value}`}</title>
              </circle>
            );
          })}
        </svg>
        
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-gray-500 -mr-8">
          <span>{maxValue.toLocaleString('he-IL')}</span>
          <span>{((maxValue + minValue) / 2).toLocaleString('he-IL')}</span>
          <span>{minValue.toLocaleString('he-IL')}</span>
        </div>
      </div>
      
      {/* X-axis labels */}
      <div className="flex justify-between mt-4 text-xs text-gray-500">
        {data.map((item, index) => (
          <span key={index} className={index % 2 === 0 ? '' : 'hidden sm:block'}>
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
};

/**
 * Simple Bar Chart Component
 */
export const BarChart = ({ data, title, color = '#10B981' }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg">
        <p className="text-gray-500">אין נתונים להצגה</p>
      </div>
    );
  }

  const maxValue = Math.max(...data.map(d => d.value));

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      {title && <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>}
      
      <div className="space-y-4">
        {data.map((item, index) => (
          <div key={index} className="flex items-center">
            <div className="w-20 text-sm font-medium text-gray-700 text-right">
              {item.label}
            </div>
            <div className="flex-1 mx-4">
              <div className="bg-gray-200 rounded-full h-6 relative overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-1000 ease-out relative"
                  style={{
                    width: `${(item.value / maxValue) * 100}%`,
                    backgroundColor: color,
                    background: `linear-gradient(90deg, ${color} 0%, ${color}CC 100%)`
                  }}
                >
                  {item.value > maxValue * 0.1 && (
                    <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white text-xs font-medium">
                      {item.value.toLocaleString('he-IL')}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="w-16 text-sm text-gray-500 text-left">
              {item.value < maxValue * 0.1 ? item.value.toLocaleString('he-IL') : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Donut Chart Component
 */
export const DonutChart = ({ data, title, centerText }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg">
        <p className="text-gray-500">אין נתונים להצגה</p>
      </div>
    );
  }

  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  let cumulativePercentage = 0;
  const arcs = data.map((item, index) => {
    const percentage = (item.value / total) * 100;
    const startAngle = cumulativePercentage * 3.6; // Convert to degrees
    const endAngle = (cumulativePercentage + percentage) * 3.6;
    
    cumulativePercentage += percentage;
    
    const largeArcFlag = percentage > 50 ? 1 : 0;
    const startX = 50 + 30 * Math.cos((startAngle - 90) * Math.PI / 180);
    const startY = 50 + 30 * Math.sin((startAngle - 90) * Math.PI / 180);
    const endX = 50 + 30 * Math.cos((endAngle - 90) * Math.PI / 180);
    const endY = 50 + 30 * Math.sin((endAngle - 90) * Math.PI / 180);
    
    const innerStartX = 50 + 15 * Math.cos((startAngle - 90) * Math.PI / 180);
    const innerStartY = 50 + 15 * Math.sin((startAngle - 90) * Math.PI / 180);
    const innerEndX = 50 + 15 * Math.cos((endAngle - 90) * Math.PI / 180);
    const innerEndY = 50 + 15 * Math.sin((endAngle - 90) * Math.PI / 180);
    
    const pathData = [
      `M ${startX} ${startY}`,
      `A 30 30 0 ${largeArcFlag} 1 ${endX} ${endY}`,
      `L ${innerEndX} ${innerEndY}`,
      `A 15 15 0 ${largeArcFlag} 0 ${innerStartX} ${innerStartY}`,
      'Z'
    ].join(' ');

    return {
      path: pathData,
      color: colors[index % colors.length],
      item,
      percentage: percentage.toFixed(1)
    };
  });

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      {title && <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>}
      
      <div className="flex items-center justify-between">
        {/* Chart */}
        <div className="relative">
          <svg width="200" height="200" viewBox="0 0 100 100" className="w-48 h-48">
            {arcs.map((arc, index) => (
              <path
                key={index}
                d={arc.path}
                fill={arc.color}
                className="hover:opacity-80 cursor-pointer transition-opacity"
              >
                <title>{`${arc.item.label}: ${arc.percentage}%`}</title>
              </path>
            ))}
          </svg>
          
          {/* Center text */}
          {centerText && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{centerText.value}</div>
                <div className="text-sm text-gray-500">{centerText.label}</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Legend */}
        <div className="flex-1 mr-6">
          <div className="space-y-2">
            {data.map((item, index) => (
              <div key={index} className="flex items-center">
                <div
                  className="w-4 h-4 rounded-full mr-3"
                  style={{ backgroundColor: colors[index % colors.length] }}
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{item.label}</div>
                  <div className="text-xs text-gray-500">
                    {item.value.toLocaleString('he-IL')} ({((item.value / total) * 100).toFixed(1)}%)
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Metric Card with Trend
 */
export const MetricCard = ({ title, value, change, trend, icon: Icon, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    purple: 'bg-purple-500',
    indigo: 'bg-indigo-500'
  };

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mb-2">{value}</p>
          
          {change !== undefined && change !== null && (
            <div className="flex items-center">
              {change >= 0 ? (
                <svg className="w-4 h-4 text-green-500 ml-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-red-500 ml-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
              <span className={`text-sm font-medium ml-1 ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {Math.abs(change)}%
              </span>
              <span className="text-sm text-gray-500 mr-2">מהחודש הקודם</span>
            </div>
          )}
          
          {(change === null || change === undefined) && (
            <div className="flex items-center">
              <span className="text-sm text-gray-500">אין נתונים היסטוריים להשוואה</span>
            </div>
          )}
          
          {/* Mini trend sparkline */}
          {trend && trend.length > 0 && (
            <div className="mt-2">
              <svg viewBox="0 0 100 20" className="w-20 h-5">
                <polyline
                  fill="none"
                  stroke={change >= 0 ? '#10B981' : '#EF4444'}
                  strokeWidth="2"
                  points={trend.map((value, index) => `${(index / (trend.length - 1)) * 100},${20 - (value / Math.max(...trend)) * 15}`).join(' ')}
                />
              </svg>
            </div>
          )}
        </div>
        
        {Icon && (
          <div className={`p-3 rounded-full ${colorClasses[color]} ml-4`}>
            <Icon className="h-8 w-8 text-white" />
          </div>
        )}
      </div>
    </div>
  );
};
